import * as React from "react";
import { Button as FluentButton, Combobox, Option } from "@fluentui/react-components";
import Button from "../Button";
import type { TemplateDataReferenceMode } from "../TemplateWorkbookService";
import { SectionHeader, type TemplateTabViewStyles } from "./TemplateTabViewStyles";
import type { RoleItem } from "./TemplateTabViewTypes";

type TemplateSettingsSectionProps = {
  styles: TemplateTabViewStyles;
  templateName: string;
  setTemplateName: (value: string) => void;
  setHasGeneratedWorkbook: (value: boolean) => void;
};

export const TemplateSettingsSection: React.FC<TemplateSettingsSectionProps> = ({
  styles,
  templateName,
  setTemplateName,
  setHasGeneratedWorkbook,
}) => (
  <div className={styles.section}>
    <SectionHeader styles={styles} num={5} title="Template settings" />
    <div style={{ fontSize: "11px", color: "#6D6E71", padding: "6px 12px 8px 12px", borderBottom: "0.25pt solid #E8F0F0" }}>
      Provide a name for this template.
    </div>
    <div className={styles.sectionBody}>
      <label htmlFor="tpl-name" className={styles.fieldLabel}>Template name *</label>
      <input
        id="tpl-name"
        type="text"
        autoComplete="off"
        placeholder="e.g., Jobs Analyzer (My Template)"
        value={templateName}
        onChange={(event) => {
          setTemplateName(event.target.value);
          setHasGeneratedWorkbook(false);
        }}
        className={styles.templateField}
      />
    </div>
  </div>
);

type TemplateGenerateSectionProps = {
  styles: TemplateTabViewStyles;
  isConfigurationLocked: boolean;
  activeEmptyVisibleSheetName: string;
  dataReferenceMode: TemplateDataReferenceMode;
  setDataReferenceMode: (value: TemplateDataReferenceMode) => void;
  setHasGeneratedWorkbook: (value: boolean) => void;
  isFormValid: boolean;
  isGenerating: boolean;
  isSaving: boolean;
  handleGenerateWorkbook: () => Promise<void>;
  handleClearAll: () => void;
};

export const TemplateGenerateSection: React.FC<TemplateGenerateSectionProps> = ({
  styles,
  isConfigurationLocked,
  activeEmptyVisibleSheetName,
  dataReferenceMode,
  setDataReferenceMode,
  setHasGeneratedWorkbook,
  isFormValid,
  isGenerating,
  isSaving,
  handleGenerateWorkbook,
  handleClearAll,
}) => (
  <div className={styles.section}>
    <SectionHeader styles={styles} num={6} title="Generate workbook" />
    <div style={{ fontSize: "11px", color: "#6D6E71", padding: "6px 12px 8px 12px", borderBottom: "0.25pt solid #E8F0F0" }}>
      Generate the workbook template structure based on the configuration above.
    </div>
    <div className={styles.sectionBody}>
      {activeEmptyVisibleSheetName && (
        <div style={{ marginBottom: "10px" }}>
          <label className={styles.fieldLabel} htmlFor="data-reference-mode">
            Add data reference to active sheet ({activeEmptyVisibleSheetName})
          </label>
          <select
            id="data-reference-mode"
            className={styles.templateField}
            value={dataReferenceMode}
            onChange={(event) => {
              setDataReferenceMode(event.target.value as TemplateDataReferenceMode);
              setHasGeneratedWorkbook(false);
            }}
            disabled={isConfigurationLocked}
          >
            <option value="standardTable">Add data reference as standard table</option>
            <option value="none">Do not add data reference</option>
          </select>
        </div>
      )}
      <div className={styles.actionBar} style={{ paddingTop: 0, marginTop: 0, borderTop: "none" }}>
        <FluentButton
          appearance="primary"
          size="small"
          className={styles.primaryButton}
          onClick={handleGenerateWorkbook}
          disabled={isConfigurationLocked || !isFormValid || isGenerating || isSaving}
          style={{ flex: 1 }}
        >
          {isGenerating ? "Generating..." : "Generate Workbook"}
        </FluentButton>
        <Button
          label="Clear All"
          onClick={handleClearAll}
          disabled={isConfigurationLocked || isGenerating || isSaving}
        />
      </div>
    </div>
  </div>
);

type TemplateSaveSectionProps = {
  styles: TemplateTabViewStyles;
  isConfigurationLocked: boolean;
  roles: RoleItem[];
  selectedRoles: string[];
  setSelectedRoles: (value: string[]) => void;
  loadingRoles: boolean;
  hasGeneratedWorkbook: boolean;
  templateName: string;
  isGenerating: boolean;
  isSaving: boolean;
  handleSaveTemplate: () => Promise<void>;
};

export const TemplateSaveSection: React.FC<TemplateSaveSectionProps> = ({
  styles,
  isConfigurationLocked,
  roles,
  selectedRoles,
  setSelectedRoles,
  loadingRoles,
  hasGeneratedWorkbook,
  templateName,
  isGenerating,
  isSaving,
  handleSaveTemplate,
}) => (
  <div className={styles.section}>
    <SectionHeader styles={styles} num={7} title="Share and save" />
    <div style={{ fontSize: "11px", color: "#6D6E71", padding: "6px 12px 8px 12px", borderBottom: "0.25pt solid #E8F0F0" }}>
      Select roles to share this template, or save it without roles.
    </div>
    <div className={styles.sectionBody}>
      <label className={styles.fieldLabel} htmlFor="roles-combo">Share with roles</label>
      <Combobox
        id="roles-combo"
        autoComplete="off"
        multiselect
        selectedOptions={selectedRoles}
        value={
          selectedRoles.length > 0
            ? selectedRoles
                .map((roleId) => roles.find((role) => role.id === roleId)?.name || roleId)
                .join(", ")
            : ""
        }
        onOptionSelect={(_, data) => setSelectedRoles(data.selectedOptions)}
        placeholder="Select roles..."
        disabled={isConfigurationLocked || !hasGeneratedWorkbook || !templateName.trim() || isSaving || isGenerating || loadingRoles}
        listbox={{ style: { maxHeight: "200px", overflowY: "auto" } }}
        style={{ width: "160px", fontSize: "11px", marginBottom: "10px" }}
        positioning={{
          position: "below",
          align: "start",
          pinned: true,
          autoSize: "width",
        }}
      >
        {loadingRoles ? (
          <Option disabled>Loading roles...</Option>
        ) : roles.length === 0 ? (
          <Option disabled>No roles available</Option>
        ) : (
          roles.map((role) => (
            <Option key={role.id} value={role.id} text={role.name}>{role.name}</Option>
          ))
        )}
      </Combobox>
      <div style={{ fontSize: "11px", color: "#8A6100", marginBottom: "10px" }}>
        Existing templates can be overwritten when this workbook is associated with a template; otherwise a new template is saved.
      </div>
      <Button
        label={isSaving ? "Saving..." : "Save Template"}
        onClick={handleSaveTemplate}
        disabled={isConfigurationLocked || !hasGeneratedWorkbook || !templateName.trim() || isSaving || isGenerating}
      />
    </div>
  </div>
);
